import { ChevronDown, ChevronUp } from 'lucide-react'

/**
 * Standardised sortable table header.
 *
 * Replaces a plain `<span>Label</span>` inside a `<th>` (or any grid-
 * header cell) with a clickable label + sort affordance. Same visual
 * recipe on every table so users learn the pattern once.
 *
 * Visual states:
 *   • Inactive  — label in default header colour + a faint stacked
 *                 ⌃⌄ icon. Hover lifts both colour and icon weight.
 *   • Active asc — label + icon flip to accent navy; up chevron is
 *                  solid, down chevron faded.
 *   • Active desc — same, but down chevron solid and up faded.
 *
 * Behaviour:
 *   • First click on a sortable column → ascending.
 *   • Click again on the same column → flip to descending.
 *   • Click a different column → that column becomes the active sort,
 *     starting ascending.
 *
 * The component is layout-agnostic — it doesn't render a `<th>` or
 * `<div>` wrapper itself, just an inline `<button>`. Drop it inside
 * whatever the table layout uses (real `<th>`, CSS-grid cell, etc.)
 * and it picks up the surrounding cell's spacing.
 *
 * Usage:
 *
 *   <th className="...">
 *     <SortableHeader
 *       field="fullName"
 *       label="Name"
 *       sortField={field}
 *       direction={direction}
 *       onSort={setSort}
 *     />
 *   </th>
 *
 *   <th className="...">User</th>     // non-sortable column — plain text
 *
 * @param {string}              field       Field id this header sorts by.
 * @param {React.ReactNode}     label       Header label (string or node).
 * @param {string|null}         sortField   Currently active field id.
 * @param {'asc'|'desc'}        direction   Currently active direction.
 * @param {(field:string)=>void} onSort     Click handler.
 * @param {string}              [className] Extra classes for the button.
 * @param {'left'|'right'}      [align]     Cell alignment. 'right' for
 *                                          numeric columns so the icon
 *                                          stays near the value.
 * @param {string}              [title]     Overrides the default
 *                                          "Sort by {label}" tooltip. Use it
 *                                          when the column itself needs
 *                                          explaining (e.g. a count whose
 *                                          population isn't obvious). Omit to
 *                                          keep the standard sort hint.
 */
export function SortableHeader({
  field,
  label,
  sortField,
  direction,
  onSort,
  className = '',
  align = 'left',
  title,
}) {
  const isActive = sortField === field
  const state = isActive ? direction : 'none'

  const alignClass =
    align === 'right' ? 'flex-row-reverse text-right' : 'flex-row text-left'

  // Tone classes — kept inline so every consumer renders identically
  // without needing to import a stylesheet. Hover tightens the colour
  // gradient; active swaps to the brand accent.
  const toneClass = isActive
    ? 'text-[color:var(--color-accent-strong)]'
    : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]'

  return (
    <button
      type="button"
      onClick={() => onSort(field)}
      aria-sort={
        state === 'asc'
          ? 'ascending'
          : state === 'desc'
            ? 'descending'
            : 'none'
      }
      title={
        title ?? `Sort by ${typeof label === 'string' ? label : field}`
      }
      className={
        `group inline-flex items-center gap-2 rounded-md -mx-1.5 -my-1 px-1.5 py-1 ` +
        `cursor-pointer transition-colors hover:bg-[color:var(--color-card-hover)] ` +
        `${alignClass} ${toneClass} ${className}`
      }
    >
      <span>{label}</span>
      {/* Stacked chevrons — the inactive state shows both at low opacity;
       *  the active state solid-renders the direction chevron and fades
       *  the other. Compact vertical stack so the icon takes ~12px wide,
       *  matching the header text height. */}
      <span className="inline-flex flex-col items-center leading-[0]">
        <ChevronUp
          size={11}
          strokeWidth={2.5}
          className={
            'transition-opacity ' +
            (state === 'asc'
              ? 'opacity-100'
              : state === 'desc'
                ? 'opacity-25'
                : 'opacity-40 group-hover:opacity-70')
          }
          style={{ marginBottom: -2 }}
        />
        <ChevronDown
          size={11}
          strokeWidth={2.5}
          className={
            'transition-opacity ' +
            (state === 'desc'
              ? 'opacity-100'
              : state === 'asc'
                ? 'opacity-25'
                : 'opacity-40 group-hover:opacity-70')
          }
          style={{ marginTop: -2 }}
        />
      </span>
    </button>
  )
}
